
import { useState, useEffect, useRef } from 'react';
import * as React from 'react';
import { Bell, X, MessageCircle, Megaphone, BookOpen, UserCheck, Heart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  type: 'subject_approved' | 'message' | 'announcement' | 'profile_approved' | 'booking_confirmed' | 'lesson_reminder' | 'general' | 'comment_reaction' | 'live_chat' | 'new_live_chat' | 'live_chat_message' | 'live_chat_started' | 'new_live_chat_started';
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  data?: any;
}

const NotificationBadge = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  // Mark all as read when modal opens
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  }, [isOpen]);

  useEffect(() => {
    // Clean up previous subscription if user changed
    if (currentUserIdRef.current !== user?.id) {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
        }
        subscriptionRef.current = null;
      }
      currentUserIdRef.current = user?.id || null;
    }

    if (user?.id && pb.authStore.isValid) {
      fetchNotifications();
      setupSubscription();
    } else {
      // Clear state if no user
      setNotifications([]);
      setUnreadCount(0);
    }

    return () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
        }
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, pb.authStore.isValid]);

  const setupSubscription = async () => {
    if (!user?.id || !pb.authStore.isValid) return;

    // Clean up existing subscription first to prevent duplicates
    if (subscriptionRef.current) {
      try {
        subscriptionRef.current();
      } catch (error) {
      }
      subscriptionRef.current = null;
    }

    try {
      const unsubscribe = await pb.collection('notifications').subscribe('*', (e) => {
        console.log('Notification event:', e);
        if (e.record.userId === user.id) {
          if (e.action === 'create') {
            console.log('New notification received:', e.record);
            // Check if notification already exists to prevent duplicates
            setNotifications(prev => {
              const exists = prev.some(n => n.id === e.record.id);
              if (exists) {
                console.log('Notification already exists, skipping duplicate');
                return prev;
              }
              return [e.record as unknown as Notification, ...prev];
            });
            if (!e.record.isRead) {
              setUnreadCount(prev => prev + 1);
            }
          } else if (e.action === 'update') {
            setNotifications(prev => 
              prev.map(n => n.id === e.record.id ? e.record as unknown as Notification : n)
            );
            fetchUnreadCount();
          }
        }
      });

      subscriptionRef.current = unsubscribe;
    } catch (error) {
      console.error('Error setting up notification subscription:', error);
    }
  };

  const fetchNotifications = async () => {
    if (!user?.id || !pb.authStore.isValid) return;
    
    try {
      const records = await pb.collection('notifications').getList(1, 50, {
        filter: `userId = "${user.id}"`,
        sort: '-createdAt'
      });
      console.log('Fetched notifications:', records.items);
      setNotifications(records.items as unknown as Notification[]);
      fetchUnreadCount();
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    if (!user?.id || !pb.authStore.isValid) return;
    
    try {
      const unreadRecords = await pb.collection('notifications').getList(1, 1, {
        filter: `userId = "${user.id}" && isRead = false`
      });
      setUnreadCount(unreadRecords.totalItems);
    } catch (error) {
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!pb.authStore.isValid) return;
    
    try {
      await pb.collection('notifications').update(notificationId, {
        isRead: true
      });
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    if (!pb.authStore.isValid) return;
    
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      for (const notification of unreadNotifications) {
        await pb.collection('notifications').update(notification.id, {
          isRead: true
        });
      }
      setUnreadCount(0);
    } catch (error) {
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // Handle navigation for reschedule requests
    if (notification.data?.approvalLink) {
      const url = new URL(notification.data.approvalLink);
      navigate(url.pathname);
      setIsOpen(false);
      return;
    }

    // Handle navigation based on notification type
    switch (notification.type) {
      case 'comment_reaction':
        if (notification.data?.postId) {
          navigate(`/inspire-posts?post=${notification.data.postId}&comment=${notification.data.commentId}`);
        }
        break;
      case 'live_chat':
      case 'new_live_chat':
      case 'live_chat_message':
      case 'live_chat_started':
      case 'new_live_chat_started':
        // Navigate based on user role
        if (user?.role === 'admin') {
          navigate('/admin/live-chat-support');
        } else if (user?.role === 'assistant') {
          navigate('/live-chat-support');
        } else {
          // Students/tutors go to their own live chat
          navigate('/live-chat');
        }
        break;
      case 'message':
        navigate('/messages');
        break;
      case 'booking_confirmed':
        navigate('/schedule');
        break;
      case 'lesson_reminder':
        navigate('/lessons');
        break;
      case 'subject_approved':
      case 'profile_approved':
        navigate('/profile');
        break;
      case 'announcement':
        navigate('/announcements');
        break;
      default:
        // For unknown types, stay on current page
        break;
    }
    
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'subject_approved':
        return <BookOpen className="h-4 w-4 text-green-600" />;
      case 'live_chat':
      case 'new_live_chat':
      case 'live_chat_message':
      case 'live_chat_started':
      case 'new_live_chat_started':
        return <MessageCircle className="h-4 w-4 text-green-600" />;
      case 'message':
        return <MessageCircle className="h-4 w-4 text-blue-600" />;
      case 'announcement':
        return <Megaphone className="h-4 w-4 text-purple-600" />;
      case 'profile_approved':
        return <UserCheck className="h-4 w-4 text-green-600" />;
      case 'booking_confirmed':
      case 'lesson_reminder':
        return <Bell className="h-4 w-4 text-orange-600" />;
      case 'comment_reaction':
        return <Heart className="h-4 w-4 text-red-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'subject_approved':
      case 'profile_approved':
        return 'border-green-200 bg-green-50';
      case 'live_chat':
      case 'new_live_chat':
      case 'live_chat_message':
      case 'live_chat_started':
      case 'new_live_chat_started':
        return 'border-green-200 bg-green-50';
      case 'message':
        return 'border-blue-200 bg-blue-50';
      case 'announcement':
        return 'border-purple-200 bg-purple-50';
      case 'booking_confirmed':
      case 'lesson_reminder':
        return 'border-orange-200 bg-orange-50';
      case 'comment_reaction':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Don't render if no user
  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-7 w-7 p-0">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && !isOpen && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 z-[99999]" align="end" side="bottom">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="p-2">
              {notifications.map((notification) => (
                <Card 
                  key={notification.id} 
                  className={`mb-2 cursor-pointer transition-colors hover:bg-gray-50 ${
                    !notification.isRead ? getNotificationColor(notification.type) : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getNotificationIcon(notification.type)}
                         <div className="flex-1">
                          <h4 className="font-medium text-sm">{notification.title}</h4>
                          <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                          {notification.data?.approvalLink && (
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="text-xs p-0 h-auto mt-1 text-blue-600 hover:text-blue-700"
                            >
                              Click to approve or reject →
                            </Button>
                          )}
                          <p className="text-xs text-gray-400 mt-2">{formatDate(notification.createdAt)}</p>
                        </div>
                      </div>
                      {!notification.isRead && (
                        <div className="h-2 w-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBadge;
